<template>
    <EditWindow heading="Edit student" :name="full_name(student.user)" @close="emit('close')">

        <EditSection title="Account">
            <EditAccount :owner="student"/>
            <EditField :object="student" field="oneclick" label="WhatsApp class updates" type="checkbox" wide
                       hint="When the teacher marks a class, the student gets a WhatsApp with its status, the hours used and whether they have paid. “Send Login Info” turns this on."/>
        </EditSection>

        <EditSection title="Classes">
            <EditField :object="student" field="level" label="Level" wide
                       hint="Free text - the level and the book/unit they are on both go here."/>
        </EditSection>

        <EditSection title="Billing">
            <EditField :object="student" field="payment_plan" label="Payment plan" type="select"
                       :options="payment_plans" :check="plan_warning"/>
            <EditField :object="student" field="package_hours" label="Package hours" type="number" step="0.5"/>
            <EditField :object="student" field="hourly_rate" label="Hourly rate" type="number" step="0.01"/>
            <EditField :object="student" field="monthly_rate" label="Monthly rate" type="number" step="0.01"/>
            <EditField :object="student" field="payment_method" label="Payment method" type="combobox"
                       :options="payment_methods" :read="method_of" :write="with_method"
                       hint="Pick one or type another. Who pays is added in front automatically."/>
            <EditField :object="student" field="payment_method" label="Pays at the end of the period" type="checkbox"
                       :read="pays_at_end" :write="with_end_flag"
                       hint="Billing and collection depend on it. Saved as @end on the payment method."/>
            <EditField :object="student" field="responsible_for_payment" label="Who pays" :suggestions="payers"/>
            <EditField :object="student" field="responsible_for_payment_contact" label="Payer's contact"/>
        </EditSection>

        <EditSection title="About">
            <EditField :object="student" field="organization" label="Organization" :suggestions="organizations"/>
            <EditField :object="student" field="profession" label="Profession"/>
            <EditField :object="student" field="sale_origin" label="Sale origin" :suggestions="sale_origins"/>
        </EditSection>

        <EditSection title="Notes">
            <EditField :object="student" field="comments" label="Comments" type="textarea"/>
        </EditSection>

    </EditWindow>
</template>


<script setup>

    import {ref,onMounted} from 'vue'
    import EditWindow from '@/components/edit/EditWindow.vue'
    import EditSection from '@/components/edit/EditSection.vue'
    import EditField from '@/components/edit/EditField.vue'
    import EditAccount from '@/components/edit/EditAccount.vue'
    import {load_options,full_name} from '@/components/edit/helpers.js'

    defineProps({
        student:{type:Object,required:true},
    })
    const emit=defineEmits(['close'])

    let payment_plans=ref([])
    let payment_methods=ref([])
    let payers=ref([])
    let organizations=ref([])
    let sale_origins=ref([])

    onMounted(async()=>{
        let options=await load_options()
        payment_plans.value=options.curated.payment_plans || []
        payment_methods.value=options.curated.payment_methods || []
        payers.value=options.observed['Student.responsible_for_payment'] || []
        organizations.value=options.observed['Student.organization'] || []
        sale_origins.value=options.observed['Student.sale_origin'] || []
    })

    // Billing (student_packages in operation/custom_functions.py) only handles plans that
    // contain "monthly" or "package"; with any other plan, confirming a class crashes.
    function plan_warning(plan){
        let text=String(plan || '').toLowerCase()
        if(text.includes('monthly') || text.includes('package')){
            return ''
        }
        return 'Billing only understands Package or a Monthly plan - confirming this student\'s classes fails until one is picked.'
    }

    // payment_method holds three things: `<who pays> → <method> @end`. Student.save() puts who
    // pays in front (some rows have it typed by hand with "Who pays" empty), and billing reads
    // @end - they pay at the end of the period - straight out of the string. The dropdown and
    // the switch each change only their own part and keep the rest as stored.
    const PAYER_SEPARATOR='→'
    const END_FLAG='@end'

    function payment_parts(value){
        let text=String(value || '')
        let end=text.includes(END_FLAG)
        text=text.split(END_FLAG).join('')
        let cut=text.lastIndexOf(PAYER_SEPARATOR)
        return {
            payer:text.slice(0,cut+1).trim(),      // up to and including the arrow, '' without one
            method:text.slice(cut+1).trim(),
            end:end,
        }
    }

    function join_payment(parts){
        return [parts.payer,parts.method,parts.end ? END_FLAG : ''].filter(Boolean).join(' ')
    }

    function method_of(value){
        return payment_parts(value).method
    }

    function pays_at_end(value){
        return payment_parts(value).end
    }

    // Typing @end into the box, the old way, still means they pay at the end.
    function with_method(method,stored){
        let parts=payment_parts(stored)
        let typed=String(method)
        parts.method=typed.split(END_FLAG).join('').trim()
        parts.end=parts.end || typed.includes(END_FLAG)
        return join_payment(parts)
    }

    function with_end_flag(end,stored){
        let parts=payment_parts(stored)
        parts.end=end
        return join_payment(parts)
    }

</script>
